import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertTriangle, ArrowRight } from 'lucide-react';
import { useResizeVolume } from '@/service/gpu-instance/volume-hook';
import type { Volume } from '@/service/gpu-instance/volume-type';
import { toast } from 'sonner';

interface ResizeVolumeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  volume: Volume | null;
  onSuccess?: () => void;
}

export const ResizeVolumeDialog = ({
  open,
  onOpenChange,
  volume,
  onSuccess,
}: ResizeVolumeDialogProps) => {
  const [newSize, setNewSize] = useState(0);
  const resizeVolume = useResizeVolume();

  useEffect(() => {
    if (volume) {
      setNewSize(volume.requestedSize);
    }
  }, [volume]);

  if (!volume) return null;

  const minSize = volume.requestedSize;
  const maxSize = 10000;
  const isValid = newSize > minSize;
  const additionalSize = newSize - volume.requestedSize;
  const additionalCost = additionalSize * volume.storageClass.pricePerGBMonth;

  const handleResize = async () => {
    if (!isValid) return;

    try {
      await resizeVolume.mutateAsync({
        volumeId: volume.id,
        newSize,
      });
      toast.success('Volume resize initiated', {
        description: `Volume "${volume.name}" is being expanded to ${newSize} GB.`,
      });
      onOpenChange(false);
      onSuccess?.();
    } catch {
      toast.error('Failed to resize volume');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Resize Volume</DialogTitle>
          <DialogDescription>
            Expand the storage capacity of "{volume.name}". Volume shrinking is not supported.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Volume expansion is an online operation. Your data will remain accessible during the resize.
            </AlertDescription>
          </Alert>

          {/* Current vs New Size */}
          <div className="flex items-center justify-center gap-4 rounded-lg bg-muted/50 p-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Current Size</p>
              <p className="text-2xl font-bold">{volume.requestedSize} GB</p>
            </div>
            <ArrowRight className="h-6 w-6 text-muted-foreground" />
            <div className="text-center">
              <p className="text-sm text-muted-foreground">New Size</p>
              <p className="text-2xl font-bold text-primary">{newSize} GB</p>
            </div>
          </div>

          {/* Size Slider */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>New Volume Size</Label>
              <span className="text-sm font-medium">{newSize} GB</span>
            </div>
            <Slider
              value={[newSize]}
              onValueChange={([value]) => setNewSize(value)}
              min={minSize}
              max={maxSize}
              step={50}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{minSize} GB (current)</span>
              <span>{maxSize.toLocaleString()} GB</span>
            </div>
          </div>

          {/* Usage Info */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Current Usage</span>
              <span>
                {volume.usedSize} GB / {volume.requestedSize} GB (
                {Math.round((volume.usedSize / volume.requestedSize) * 100)}%)
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Storage Class</span>
              <span>{volume.storageClass.displayName}</span>
            </div>
          </div>

          {/* Cost Estimate */}
          {isValid && (
            <div className="rounded-lg bg-muted/50 p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Additional Monthly Cost</span>
                <span className="text-lg font-bold">+${additionalCost.toFixed(2)}/mo</span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                +{additionalSize} GB x ${volume.storageClass.pricePerGBMonth}/GB
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleResize} disabled={!isValid || resizeVolume.isPending}>
            {resizeVolume.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Resize Volume
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ResizeVolumeDialog;
